import { array, fn } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';

import type { Service } from 'webapp-types';
import { PopupEvent } from 'webapp/enums/enums';
import { isAlive } from 'webapp/utils/common';
import { scaleValToAutofit } from 'webapp/utils/html';

import joinClasses from 'webapp/helpers/join-classes';
import didInsert from 'webapp/modifiers/did-insert';
import didUpdate from 'webapp/modifiers/did-update';
import visibleCheckItem from 'webapp/modifiers/visible-check-item';
import BlockDebugButtonComponent from 'webapp/components/block/debug-button';
import CommonSkeletonBarLoaderComponent from 'webapp/components/common/skeleton-bar-loader';
import CommonUiIconComponent from 'webapp/components/common/ui-icon';
import EditableModelPropertyComponent from 'webapp/components/editable-model-property';
import FormCheckboxComponent from 'webapp/components/form/checkbox';
import type { Args } from 'webapp/components/goals/item-base';
import GoalsItemBaseComponent from 'webapp/components/goals/item-base';
import GoalsProgressComponent from 'webapp/components/goals/progress';
import GroupSelectComponent from 'webapp/components/group-select';
import OwnerSelectComponent from 'webapp/components/owner-select';
import SubmitButtonComponent from 'webapp/components/submit-button';
import TeamsViewSectionRestrictedIconComponent from 'webapp/components/teams/view/section/restricted-icon';
import ToolTipComponent from 'webapp/components/tool-tip';

export default class GoalsGridItemComponent extends GoalsItemBaseComponent<Args> {
	@service('popup') declare popupService: Service.Popup;
	@service('analytics') declare analyticsService: Service.Analytics;

	@tracked contextOpened = false;
	@tracked isDataPerformanceSummaryPopupOpen = false;

	node: HTMLElement | null = null;

	get isSomethingOpen() {
		return this.contextOpened || this.isDataPerformanceSummaryPopupOpen;
	}

	get canEditGoal() {
		return this.model.canEdit;
	}

	@action
	changeDataPerformanceSummaryPopupOpenState(isOpen: boolean) {
		this.isDataPerformanceSummaryPopupOpen = isOpen;
	}

	@action
	openContextDropdown(e: PointerEvent): void {
		if (this.contextOpened) {
			return;
		}

		const attachTo = e.target as HTMLElement;
		const popup = this.popupService.createListPopup(attachTo, undefined, 'goals');

		if (popup) {
			popup.open({
				itemsList: this.options,
				doAction: (actionName: string) => {
					this.doOptionsAction(actionName);
				},
			});

			this.contextOpened = true;
			popup.on(PopupEvent.CLOSED, this, () => {
				if (isAlive(this)) {
					this.contextOpened = false;
				}
			});
		}
	}

	@action
	scaleValToAutofit(): void {
		const node = this.node;
		if (!node) {
			return;
		}

		const valEl = node.querySelector('.val'),
			minSizeEm = 0.5,
			maxSizeEm = 1;

		if (valEl) {
			scaleValToAutofit(valEl as HTMLElement, minSizeEm, maxSizeEm);
		}
	}

	@action
	override visibleChanged(isVisible: boolean): void {
		super.visibleChanged(isVisible);
	}

	@action
	setupView(node: HTMLElement): void {
		this.node = node;

		this.scaleValToAutofit();
	}
	<template>
		<li
			data-id={{@model.id}}
			class={{joinClasses
				(array
					@model.animationClass
					"card"
					"card--visualization"
					"card--goal"
					"flex-item"
					"filter-sort-item"
					this.goalData.currentProgressClass
					(if this.isSomethingOpen "contextOpened")
					(if @hideSort "hideSort")
					(if this.isSelected "selected")
				)
			}}
			{{didInsert this.setupView}}
		>
			{{#if this.isVisibleOnScreen}}

				{{#if this.showDragHandle}}
					<div class="drag" data-id={{@model.id}}>
						<CommonUiIconComponent @name="ArrowsMove" @size="md" />
					</div>
				{{/if}}

				<div class="top">
					<div class="top__left">
						{{#if this.emblemTypeName}}
							<div class="emblem emblem--{{this.emblemTypeName}}"></div>
						{{/if}}

						{{#if @batchSelect}}
							<div class="checkbox-wrapper">
								<FormCheckboxComponent
									@name="goals-grid-item"
									@selected={{this.isSelected}}
									@theme="blue"
									@onChange={{this.selectItem}}
								/>
							</div>
						{{/if}}

						<div>{{@model.timeframeText}}</div>
					</div>

					{{#if this.showActions}}
						<div class="top__right">
							{{#unless this.isSelected}}
								<div
									role="button"
									class="favorite"
									{{on "click" this.toggleFavorite}}
								>
									<CommonUiIconComponent
										@name="StarFill"
										@size="sm"
										@class={{unless @model.favorite "gray"}}
									/>
									<ToolTipComponent @text={{this.model.favoriteText}} />
								</div>
							{{/unless}}

							{{#if @isRestricted}}
								<TeamsViewSectionRestrictedIconComponent />
							{{/if}}

							{{!-- <DataPerformance::SummaryButton
                        @object={{this.dataPerformanceSummaryObject}}
                        @onPopupOpenStateChange={{this.changeDataPerformanceSummaryPopupOpenState}}
                    /> --}}

							{{#unless this.isSelected}}
								<div
									role="button"
									class="context"
									{{on "click" this.openContextDropdown}}
								>
									<CommonUiIconComponent @name="Ellipsis" @size="lg" />
								</div>
							{{/unless}}
						</div>
					{{/if}}
				</div>

				<div class="info">
					<div class="anti-stretch">
						<EditableModelPropertyComponent
							@model={{@model}}
							@canEdit={{this.canEdit}}
							@trackingObj={{this.goalRenamedTrackingObj}}
						/>
					</div>
				</div>

				<div class="visualization">
					<div
						class="number"
						{{didUpdate this.scaleValToAutofit this.goalData.comparePoint.changeStr}}
					>
						{{#if this.goalData.isLoadingData}}
							<CommonSkeletonBarLoaderComponent @type="number" @position="center" />
						{{else if this.goalData.comparePoint.changeStr}}
							<div class="wrap">
								<div class="val">
									{{this.goalData.comparePoint.changeStr}}
								</div>
							</div>
						{{/if}}
					</div>

					<div class="goal-progress-wrapper">
						<GoalsProgressComponent
							@model={{@model}}
							@goalData={{this.goalData}}
							@size="small"
						/>
					</div>
				</div>

				<div class="compare-action-area">
					<div class="compare-action-area__actions">
						<SubmitButtonComponent
							@text="View"
							@onTrigger={{fn this.doOptionsAction "viewDetails"}}
							@noDelay={{true}}
						/>
						{{#if this.canEditGoal}}
							<SubmitButtonComponent
								@text="Update status"
								@onTrigger={{fn this.doOptionsAction "updateProgress"}}
								@noDelay={{true}}
								@theme="blue-outlined"
							/>
						{{/if}}
					</div>
				</div>

				<div class="bottom">
					<OwnerSelectComponent @model={{@model}} @canEdit={{this.canEdit}} />
					<GroupSelectComponent @model={{@model}} @canEdit={{false}} />
				</div>

				<BlockDebugButtonComponent @blockId={{@model.blockId}} />

			{{else}}
				<div class="info" {{visibleCheckItem this.visibleChanged this.checkVisibility}}>
					<div class="anti-stretch">&nbsp;</div>
				</div>
			{{/if}}
		</li>
	</template>
}
